import { Label } from "@radix-ui/react-label";
import { forwardRef, useState } from "react";
import { cn } from "../lib/utils";
import { ChevronIcon } from "../icons/chevron";

interface NativeSelectProps {
  label: string;
  id: string;
  options: { value: string; label: string }[];
  rounded?: boolean;
  error?: string;
  helper?: string;
}

export const NativeSelect = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & NativeSelectProps
>(
  (
    { label, id, options, rounded = false, error, helper, className, ...props },
    ref,
  ) => {
    const [value, setValue] = useState(props.value || "");
    const isError = !!error;
    const hasValue = value !== "";

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      setValue(event.target.value);

      if (props.onChange) {
        props.onChange(event);
      }
    };

    const selectedLabel = options.find(
      (option) => option.value === value,
    )?.label;

    return (
      <div>
        <div className="relative isolate">
          <select
            ref={ref}
            id={id}
            aria-invalid={isError}
            aria-describedby={
              isError ? `${id}-error` : helper ? `${id}-helper` : undefined
            }
            className={cn(
              // Layout
              "w-full appearance-none",
              // Sizing & Dimensions
              "h-14 lg:h-16",
              // Typography
              "bb-text-body leading-0",
              // Colors
              "bg-white",
              // Borders
              "border-gray-border rounded-md border-2",
              // Spacing
              "pt-4 pr-10 pl-3 lg:pl-5",
              // States
              "focus:border-gray-2 focus:outline-none",
              "disabled:bg-gray-1 disabled:cursor-not-allowed",
              // Transitions
              "transition-all",
              // Conditional styles
              {
                "rounded-4xl pl-5": rounded,
                "border-error focus:border-error": isError,
              },
              className,
            )}
            {...props}
            onChange={handleChange}
          >
            <option value="" hidden />
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div
            className={cn(
              // Layout & Positioning
              "absolute z-20 max-w-[calc(100%-3.5rem)] truncate",
              // Typography
              "bb-text-body [font-size:min(1rem,16px)]",
              // Transitions
              "transition-all",
              // Conditional styles
              {
                "opacity-0": !hasValue,
                "opacity-100": hasValue,
                "top-5.5 left-4.5 lg:top-7 lg:left-6.5": !rounded,
                "top-5.5 left-6.5 lg:top-7 lg:left-6.5": rounded,
              },
            )}
          >
            {selectedLabel}
          </div>

          <ChevronIcon
            className={cn(
              // Layout & Positioning
              "pointer-events-none absolute top-1/2 z-30 -translate-y-1/2",
              // Colors
              "text-gray-2",
              // Conditional positioning
              {
                "right-4.5 lg:right-6.5": !rounded,
                "right-6.5 lg:right-6.5": rounded,
              },
            )}
          />

          <Label
            htmlFor={id}
            className={cn(
              // Typography overrides
              "![font-family:unset] !leading-6 ![font-weight:unset] !normal-case",
              // Layout & Positioning
              "pointer-events-none absolute z-10 max-w-[calc(100%-4rem)] truncate overflow-hidden",
              "lg:max-w-[calc(100%-5.5rem)]",
              // Colors
              "text-gray-2",
              // Horizontal positioning
              "left-4.5 lg:left-6.5",
              // Animation
              "transform-gpu transition-all ease-in-out",
              // Conditional styles
              {
                "left-6.5": rounded,
                "!bb-text-body translate-y-4 ![font-size:min(1rem,16px)] lg:translate-y-5":
                  !hasValue,
                "!bb-text-tiny translate-y-1.5 lg:translate-y-2": hasValue,
              },
            )}
          >
            {label}
          </Label>
        </div>

        {isError && (
          <div
            id={`${id}-error`}
            className="bb-text-tiny text-error mt-1 ml-0.5"
            role="alert"
          >
            {error}
          </div>
        )}

        {helper && !isError && (
          <div
            id={`${id}-helper`}
            className="bb-text-tiny text-gray-2 mt-1 ml-0.5"
          >
            {helper}
          </div>
        )}
      </div>
    );
  },
);

NativeSelect.displayName = "NativeSelect";
